import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePoints } from '../../context/PointsContext';
import { useLanguage } from '../../context/LanguageContext';
import { supabase } from '../../services/supabaseClient';
import BonusService from '../../services/bonusService';
import CountrySelector from '../ui/CountrySelector';
import WebsiteMessageComponent from '../common/WebsiteMessage';
import SimpleLanguageSwitcher from '../common/SimpleLanguageSwitcher';
import './TopNavbar.css';

// Public routes where we shouldn't show loading spinner
const PUBLIC_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password', '/auth/callback'];

interface TopNavbarProps {
  onSidebarToggle?: () => void;
}

const TopNavbar: React.FC<TopNavbarProps> = ({ onSidebarToggle }) => {
  const { user, signOut, loading } = useAuth();
  const { userPoints } = usePoints();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  // Don't show loading spinner on public routes (login, register, etc.)
  const isPublicRoute = PUBLIC_ROUTES.some(route => location.pathname.startsWith(route));
  const showLoading = loading && !isPublicRoute;
  // const [profileData, setProfileData] = useState<any>(null); // Fetched but not displayed yet
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [pointsDropdownOpen, setPointsDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [hasClaimableBonuses, setHasClaimableBonuses] = useState(false);
  const [avatarLoadFailed, setAvatarLoadFailed] = useState(false);
  const [userRole, setUserRole] = useState<string | null>(null);

  // Define callback functions before they're used in useEffect dependencies
  const checkClaimableBonuses = useCallback(async () => {
    if (!user) return;

    try {
      const bonusService = BonusService.getInstance();

      // Check daily bonus (deduplicated - only runs once per day)
      await bonusService.checkDailyLoginBonus(user.id);

      // Get bonuses with caching (won't hit DB if cache is valid)
      const bonuses = await bonusService.getClaimableBonuses(user.id);
      setHasClaimableBonuses(bonuses.length > 0);
    } catch (err) {
      console.error('Error checking claimable bonuses:', err);
    }
  }, [user]);

  const fetchUserProfile = useCallback(async () => {
    if (!user) return;
    setAvatarLoadFailed(false);

    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      if (error) throw error;
      // if (data) {
      //   setProfileData(data);
      // }
    } catch (err) {
      console.error('Error fetching user profile:', err);
    }
  }, [user]);

  // Fetch user role separately to ensure it's up-to-date from database
  useEffect(() => {
    const fetchUserRole = async () => {
      console.log('[TopNavbar] fetchUserRole called, user:', user?.id, user?.email);

      if (!user) {
        console.log('[TopNavbar] No user, clearing role');
        setUserRole(null);
        return;
      }

      try {
        console.log('[TopNavbar] Querying database for role...');
        const { data, error } = await supabase
          .from('users')
          .select('role')
          .eq('id', user.id)
          .single();

        console.log('[TopNavbar] Query result:', { data, error, role: data?.role });

        if (error) {
          console.error('[TopNavbar] Error fetching role:', error);
          setUserRole(null);
          return;
        }

        if (!error && data) {
          console.log('[TopNavbar] ✅ Setting userRole to:', data.role);
          setUserRole(data.role);
        } else {
          console.warn('[TopNavbar] ⚠️ No data returned, setting role to null');
          setUserRole(null);
        }
      } catch (err) {
        console.error('[TopNavbar] Exception fetching user role:', err);
        setUserRole(null);
      }
    };

    fetchUserRole();
  }, [user]);

  useEffect(() => {
    if (user) {
      fetchUserProfile();
      checkClaimableBonuses();

      // Subscribe to bonus updates from the service
      const bonusService = BonusService.getInstance();
      const unsubscribe = bonusService.subscribe((bonuses) => {
        setHasClaimableBonuses(bonuses.length > 0);
      });

      // Reduced polling: 30 min instead of 5 min (bonuses don't change frequently)
      // The service caches results, so this mostly just checks cache validity
      const intervalId = setInterval(checkClaimableBonuses, 30 * 60 * 1000);

      return () => {
        clearInterval(intervalId);
        unsubscribe();
      };
    }
  }, [user, fetchUserProfile, checkClaimableBonuses]);



  const handleSignOut = async () => {
    const { error } = await signOut();
    if (!error) {
      navigate('/login');
    } else {
      console.error('Sign out failed:', error);
      // If sign out fails, force a full page reload to clear stale state
      window.location.replace('/login?session_expired=true');
    }
  };

  // Track if we just handled a touch event to prevent double-firing
  const touchHandledRef = useRef(false);

  const handleSidebarToggle = useCallback((e: React.MouseEvent) => {
    // Skip if this was already handled by touch
    if (touchHandledRef.current) {
      touchHandledRef.current = false;
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    if (onSidebarToggle) {
      onSidebarToggle();
    }
  }, [onSidebarToggle]);

  const handleSidebarToggleTouch = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    touchHandledRef.current = true;
    if (onSidebarToggle) {
      onSidebarToggle();
    }
    // Reset after a short delay
    setTimeout(() => {
      touchHandledRef.current = false;
    }, 300);
  }, [onSidebarToggle]);

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
    if (pointsDropdownOpen) setPointsDropdownOpen(false);
    if (mobileMenuOpen) setMobileMenuOpen(false);
  };

  const togglePointsDropdown = () => {
    setPointsDropdownOpen(!pointsDropdownOpen);
    if (dropdownOpen) setDropdownOpen(false);
    if (mobileMenuOpen) setMobileMenuOpen(false);
  };

  const handleRocketClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Navigate to journey list where users can see or create journeys
    // navigate('/journey-list');
  };

  // const toggleMobileMenu = () => {
  //   setMobileMenuOpen(!mobileMenuOpen);
  //   if (dropdownOpen) setDropdownOpen(false);
  //   if (pointsDropdownOpen) setPointsDropdownOpen(false);
  // };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (dropdownOpen && !target.closest('.user-dropdown')) {
        setDropdownOpen(false);
      }
      if (pointsDropdownOpen && !target.closest('.points-dropdown')) {
        setPointsDropdownOpen(false);
      }
      if (mobileMenuOpen && !target.closest('.mobile-menu') && !target.closest('.mobile-menu-toggle')) {
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [dropdownOpen, pointsDropdownOpen, mobileMenuOpen]);

  const navigationItems = [
    {
      group: t('navigation.journeys'),
      icon: 'bi-signpost-2',
      color: '#3b82f6',
      dataTour: 'journey-nav',
      items: [
        { to: '/journey-list', icon: 'bi-list-ul', label: t('navigation.myJourneys') },
        { to: '/startup-journey/setup', icon: 'bi-plus-circle', label: t('navigation.createJourney') }
      ]
    },
    {
      group: t('navigation.tasks'),
      icon: 'bi-list-task',
      color: '#f59e0b',
      dataTour: 'tasks-nav',
      items: [
        { to: '/tasks', icon: 'bi-search', label: t('navigation.browseTasks') },
        { to: '/create-task', icon: 'bi-clipboard-plus', label: t('navigation.createTask') }
      ]
    },
    {
      group: t('navigation.questionnaires'),
      icon: 'bi-patch-question-fill',
      color: '#f59e0b',
      dataTour: 'questionnaires-nav',
      items: [
        { to: '/questionnaires', icon: 'bi-list-ul', label: t('navigation.takeSurveys') },
        { to: '/questionnaires/publisher', icon: 'bi-ui-checks', label: t('navigation.mySurveys') },
        { to: '/create-questionnaire', icon: 'bi-plus-circle', label: t('navigation.createQuestionnaire') }
      ]
    },
    {
      group: t('navigation.projects'),
      icon: 'bi-lightbulb',
      color: '#10b981',
      dataTour: 'projects-nav',
      items: [
        { to: '/projects', icon: 'bi-search', label: t('navigation.browseProjects') },
        { to: '/create-project', icon: 'bi-plus-circle', label: t('navigation.createProject') }
      ]
    },
    {
      group: t('navigation.teams'),
      icon: 'bi-people',
      color: '#f59e0b',
      dataTour: 'teams-nav',
      items: [
        { to: '/teams', icon: 'bi-search', label: t('navigation.browseTeams') },
        { to: '/create-team-request', icon: 'bi-people-fill', label: t('navigation.createTeam') }
      ]
    }
  ];

  if (showLoading) {
    return (
      <nav className="top-navbar">
        <div className="navbar-container">
          <div className="navbar-brand-section">
            <Link to="/" className="navbar-brand">
              <img
                src="/logos/seeddots-icon.svg"
                alt="SeedDots"
                className="brand-icon clickable-rocket"
                onClick={handleRocketClick}
                title="Go to your startup journey"
              />
              <div className="brand-text-container">
                <div className="brand-title">
                  <span className="brand-seed">Seed</span><span className="brand-dots">Dots</span>
                </div>
                <div className="brand-tagline">From seed to success.</div>
              </div>
            </Link>
          </div>
          <div className="navbar-loading">
            <div className="loading-ring"></div>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="top-navbar">
      <div className="navbar-container">        {/* Brand Section */}        <div className="navbar-brand-section">
        {/* Sidebar Toggle */}
        {user && onSidebarToggle && (
          <button
            className="sidebar-toggle-btn"
            onClick={handleSidebarToggle}
            onTouchEnd={handleSidebarToggleTouch}
            aria-label={t('header.toggleSidebar')}
          >
            <i className="bi bi-list"></i>
          </button>
        )}

        <Link to="/" className="navbar-brand">
          <img
            src="/logos/seeddots-icon.svg"
            alt="SeedDots"
            className="brand-icon clickable-rocket"
            onClick={handleRocketClick}
            title={t('navigation.myJourneys')}
          />
          <div className="brand-text-container">
            <div className="brand-title">
              <span className="brand-seed">Seed</span><span className="brand-dots">Dots</span>
            </div>
            <div className="brand-tagline">From seed to success.</div>
          </div>
        </Link>
      </div>

        {/* Website Message - Center section */}
        <div className="navbar-center">
          <WebsiteMessageComponent />
        </div>

        {/* Right Section */}
        <div className="navbar-right">
          {/* Country Selector */}
          <div className="country-selector-wrapper">
            <CountrySelector />
          </div>

          {/* Language Switcher */}
          <div className="language-switcher-wrapper">
            <SimpleLanguageSwitcher />
          </div>

          {user ? (
            <>
              {/* Points Display */}
              <div className="points-dropdown">
                <button className="points-display" onClick={togglePointsDropdown}>
                  <div className="points-content">
                    <i className="bi bi-star-fill points-icon"></i>
                    <span className="points-value">{userPoints.toLocaleString()}</span>
                    <span className="points-label">{t('navigation.points')}</span>
                  </div>
                  {hasClaimableBonuses && (
                    <div className="bonus-indicator">
                      <i className="bi bi-gift-fill"></i>
                    </div>
                  )}
                  <i className="bi bi-chevron-down dropdown-chevron"></i>
                </button>

                {pointsDropdownOpen && (
                  <div className="modern-dropdown points-menu">
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setPointsDropdownOpen(false);
                        navigate('/claim-bonuses');
                      }}
                    >
                      <i className="bi bi-gift dropdown-item-icon"></i>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">{t('header.claimBonuses')}</span>
                        <span className="dropdown-item-desc">{t('header.getDailyRewards')}</span>
                      </div>
                    </button>
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setPointsDropdownOpen(false);
                        navigate('/points-history');
                      }}
                    >
                      <i className="bi bi-clock-history dropdown-item-icon"></i>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">{t('header.pointsHistory')}</span>
                        <span className="dropdown-item-desc">{t('header.viewTransactions')}</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {/* User Profile */}
              <div className="user-dropdown">
                <button className="user-profile-button" onClick={toggleDropdown}>
                  <div className="user-avatar">
                    {user?.avatar_url && !avatarLoadFailed ? (
                      <img
                        src={user.avatar_url}
                        alt="Profile"
                        className="avatar-image"
                        onError={() => setAvatarLoadFailed(true)}
                      />
                    ) : (
                      <div className="avatar-placeholder">
                        {(user?.display_name || user?.email || '?').charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="user-info">
                    <span className="user-name">{user?.display_name || user?.email}</span>
                    <span className="user-role">{t('header.member')}</span>
                  </div>
                  <i className="bi bi-chevron-down dropdown-chevron"></i>
                </button>

                {dropdownOpen && (
                  <div className="modern-dropdown user-menu">
                    <button
                      className="dropdown-item"
                      onClick={() => {
                        setDropdownOpen(false);
                        navigate('/profile');
                      }}
                    >
                      <i className="bi bi-person dropdown-item-icon"></i>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">{t('header.myProfile')}</span>
                        <span className="dropdown-item-desc">{t('header.manageAccount')}</span>
                      </div>
                    </button>
                    {(() => {
                      console.log('[TopNavbar] Checking admin menu visibility - userRole:', userRole, 'isAdmin:', userRole === 'admin');
                      return userRole === 'admin';
                    })() && (
                        <button
                          className="dropdown-item"
                          onClick={() => {
                            console.log('[TopNavbar] Admin Settings clicked, navigating to /settings');
                            setDropdownOpen(false);
                            navigate('/settings');
                          }}
                        >
                          <i className="bi bi-gear dropdown-item-icon"></i>
                          <div className="dropdown-item-content">
                            <span className="dropdown-item-title">{t('navigation.adminSettings')}</span>
                            <span className="dropdown-item-desc">{t('navigation.platformConfiguration')}</span>
                          </div>
                        </button>
                      )}
                    <div className="dropdown-divider"></div>
                    <button onClick={handleSignOut} className="dropdown-item logout-item">
                      <i className="bi bi-box-arrow-right dropdown-item-icon"></i>
                      <div className="dropdown-item-content">
                        <span className="dropdown-item-title">{t('common.signOut')}</span>
                        <span className="dropdown-item-desc">{t('header.seeYouLater')}</span>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="auth-buttons">
              <Link to="/login" className="btn-modern btn-secondary">
                <i className="bi bi-box-arrow-in-right"></i>
                <span>{t('common.signIn')}</span>
              </Link>
              <Link to="/register" className="btn-modern btn-primary">
                <i className="bi bi-person-plus"></i>
                <span>{t('common.signUp')}</span>
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {user && (
        <div className={`mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-menu-header">
            <h3>{t('navigation.home')}</h3>
            <button className="close-mobile-menu" onClick={() => setMobileMenuOpen(false)}>
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          <div className="mobile-nav-content">
            <NavLink
              to="/"
              className={({ isActive }) => `mobile-nav-item ${isActive ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <i className="bi bi-house-door nav-icon"></i>
              <span>{t('navigation.home')}</span>
            </NavLink>

            {navigationItems.map((navGroup, index) => (
              <div key={index} className="mobile-nav-group" data-tour={navGroup.dataTour}>
                <div className="mobile-nav-group-title">
                  <i className={`bi ${navGroup.icon} nav-icon`} style={{ color: navGroup.color }}></i>
                  <span>{navGroup.group}</span>
                </div>
                {navGroup.items.map((item, itemIndex) => (
                  <NavLink
                    key={itemIndex}
                    to={item.to}
                    className={({ isActive }) => `mobile-nav-item sub-item ${isActive ? 'active' : ''}`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <i className={`bi ${item.icon} nav-icon`}></i>
                    <span>{item.label}</span>
                  </NavLink>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && <div className="mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)}></div>}
    </nav>
  );
};

export default TopNavbar;
